import { platform } from '../platform.ts';
import { audioAnalysisPreferred } from './quality.ts';

/**
 * Shared audio graph for every tile.
 *
 * Tiles cannot simply be `muted`: a muted media element delivers no samples to
 * WebAudio at all (measured: RMS exactly 0 while muted, 0.06 with gain 0). Since
 * silencing a tile and listening to it are both required -- one for the user,
 * one for the excitement detector -- silence has to come from a gain node
 * instead, with the element itself left unmuted.
 *
 * Routing through WebAudio has a cost: if the AudioContext cannot start, nothing
 * is audible at all. So an element is only rerouted once the context is actually
 * running, and `.muted` stays the fallback until then.
 */

export interface TrackHandle {
  setGain(value: number): void;
  /** Linear RMS of the most recent frame, 0..1. */
  level(): number;
  /** Short state for the telemetry: what the analyser currently hears. */
  describe(): string;
  release(): void;
}

const SMOOTHING = 0.6;

const dbOf = (rms: number) => (rms > 0 ? `${Math.round(20 * Math.log10(rms))}dB` : 'stumm');

let context: AudioContext | null = null;
let resumePending = false;
/**
 * The graph only exists on desktops with the analysis on; everywhere else it
 * is never created, and every tile keeps the plain `.muted` path.
 */
let disabled = !audioAnalysisPreferred() || platform !== 'desktop';
/**
 * Why the graph is off from the start, for the technical display. Not the case
 * on TVs and phones with analysis on: there the proxy measures the levels
 * instead (serverLevels.ts) -- routing through WebAudio made a Google TV
 * stutter every two seconds, and a captureStream copy was silent there.
 */
export const disabledByPreference = !audioAnalysisPreferred();
const disableListeners = new Set<(reason: string) => void>();

/** One source node per element, for life -- the API allows no second one. */
const sources = new WeakMap<HTMLMediaElement, MediaElementAudioSourceNode>();

function ensureContext(): AudioContext | null {
  if (context) return context;
  try {
    context = new AudioContext();
  } catch {
    return null;
  }
  return context;
}

/**
 * Browsers start an AudioContext suspended until the user interacts. Hooking
 * the first interaction is what makes tile audio work without asking anyone to
 * press a dedicated button.
 */
export function primeOnUserGesture(): void {
  if (disabled || resumePending) return;
  resumePending = true;

  const resume = () => {
    const ctx = ensureContext();
    void ctx?.resume().catch(() => {});
    if (ctx?.state === 'running') detach();
  };
  const detach = () => {
    window.removeEventListener('pointerdown', resume);
    window.removeEventListener('keydown', resume);
  };

  window.addEventListener('pointerdown', resume);
  window.addEventListener('keydown', resume);
  resume();
}

export function isRunning(): boolean {
  return !disabled && context?.state === 'running';
}

export function isDisabled(): boolean {
  return disabled;
}

/**
 * Permanently stops routing audio through the graph.
 *
 * A media element connected to a MediaElementAudioSourceNode has its playback
 * driven by the audio graph. If that graph stops advancing -- a device that
 * never starts, a renderer that throttles it -- every connected element freezes
 * with it, which is far worse than losing the level meters. So the moment
 * playback stalls, the whole feature steps aside and `.muted` takes over again.
 */
export function disable(reason: string): void {
  if (disabled) return;
  disabled = true;
  for (const listener of disableListeners) listener(reason);
  void context?.close().catch(() => {});
  context = null;
}

export function onDisabled(listener: (reason: string) => void): () => void {
  disableListeners.add(listener);
  return () => disableListeners.delete(listener);
}

/**
 * Routes an element through the graph. Returns null when the context is not
 * running, in which case the caller must keep using `.muted` -- routing into a
 * suspended context would silence the element entirely.
 */
export function attach(element: HTMLMediaElement): TrackHandle | null {
  if (disabled) return null;
  const ctx = ensureContext();
  if (!ctx || ctx.state !== 'running') return null;

  let source = sources.get(element);
  if (!source) {
    try {
      source = ctx.createMediaElementSource(element);
      sources.set(element, source);
    } catch {
      // Already routed by an earlier handle, or the element is not eligible.
      return null;
    }
  }

  const analyser = ctx.createAnalyser();
  analyser.fftSize = 1024;
  analyser.smoothingTimeConstant = SMOOTHING;

  const gain = ctx.createGain();
  gain.gain.value = 0;

  source.connect(analyser);
  analyser.connect(gain);
  gain.connect(ctx.destination);

  const buffer = new Float32Array(analyser.fftSize);
  let released = false;

  return {
    setGain(value: number) {
      if (released) return;
      // A short ramp instead of a jump, so switching the audio source does not
      // click.
      const now = ctx.currentTime;
      gain.gain.cancelScheduledValues(now);
      gain.gain.setTargetAtTime(Math.max(0, Math.min(1, value)), now, 0.03);
    },
    level() {
      if (released) return 0;
      analyser.getFloatTimeDomainData(buffer);
      let sum = 0;
      for (let i = 0; i < buffer.length; i++) sum += buffer[i]! * buffer[i]!;
      return Math.sqrt(sum / buffer.length);
    },
    describe() {
      return `graph ${dbOf(this.level())}`;
    },
    release() {
      if (released) return;
      released = true;
      try {
        // The source node stays connected to the element for its lifetime; only
        // this handle's branch is torn down.
        source!.disconnect(analyser);
        analyser.disconnect();
        gain.disconnect();
      } catch {
        /* already torn down */
      }
    },
  };
}

